/**
 * CampaignKeyArtShareDialog — bundle + share + optional email.
 *
 * Props:
 *   open
 *   onClose
 *   campaignId
 *   selectedIds   - array of campaign_key_art item ids
 *   selectedItems - full item objects for the selectedIds. Used to
 *                   compute total size client-side so the "Attach to
 *                   email" gate is decidable UPFRONT, before any
 *                   zipping. Sum-of-source-bytes is conservative —
 *                   compression might shrink the actual payload, but
 *                   the gate is on the predictable number.
 *   onSent        - (response) => void   called after a successful POST
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, CircularProgress, Alert, Chip,
  FormControlLabel, Checkbox,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import api from '../services/api';
import { CANONICAL_ORIGIN } from '../config';


// Must match backend KEY_ART_ATTACH_MAX_BYTES (15 MB). Gmail's 25 MB
// nominal cap shrinks to ~18 MB after base64 encoding overhead; we
// hold to 15 to leave headroom for headers + the html body.
const ATTACH_MAX_BYTES = 15 * 1024 * 1024;


function fmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}


export default function CampaignKeyArtShareDialog({
  open, onClose, campaignId, selectedIds = [], selectedItems = [], onSent,
}) {
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [attach, setAttach] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRecipientName('');
    setRecipientEmail('');
    setMessage('');
    setAttach(false);
    setResult(null);
    setError(null);
    setBusy(false);
    setCopied(false);
  }, [open]);

  // Source-file total — conservative size for the attach gate.
  const totalSourceBytes = (selectedItems || []).reduce((sum, it) => sum + (it?.size_bytes || 0), 0);
  const attachOverLimit = totalSourceBytes > ATTACH_MAX_BYTES;

  const handleSubmit = async (sendEmail) => {
    setBusy(true);
    setError(null);
    try {
      const payload = { item_ids: selectedIds };
      if (sendEmail) {
        if (!recipientEmail.trim()) {
          setError('Recipient email is required to send.');
          setBusy(false);
          return;
        }
        payload.recipient_email = recipientEmail.trim();
        payload.recipient_name = recipientName.trim() || undefined;
        payload.message = message.trim() || undefined;
        payload.attach = !!attach;
      }
      const r = await api.post(`/api/campaigns/${campaignId}/key-art/share`, payload);
      setResult(r.data);
      onSent?.(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not create share.');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.share_url) return;
    try {
      await navigator.clipboard.writeText(CANONICAL_ORIGIN + (result.share_url.startsWith('http') ? '' : result.share_url));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* */ }
  };

  // Build the visible URL — when share_url is relative, prefix with
  // the canonical app origin (NOT window.location.origin — see config.js
  // CANONICAL_ORIGIN docstring) so the recipient sees the new canonical
  // even when the operator is browsing on the legacy hostname.
  const visibleUrl = result?.share_url
    ? (result.share_url.startsWith('http')
        ? result.share_url
        : `${CANONICAL_ORIGIN}${result.share_url}`)
    : '';

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {selectedIds.length === 1 ? 'Share key-art file' : `Bundle ${selectedIds.length} files as zip`}
      </DialogTitle>
      <DialogContent>
        {!result && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {selectedIds.length === 1
                ? 'Generates a download link for the selected file.'
                : `Server zips the ${selectedIds.length} selected files and gives you a download link.`}
            </Typography>
            <TextField
              label="Recipient name (optional)"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Recipient email (optional)"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="leave blank to just copy the link"
              fullWidth
            />
            <TextField
              label="Message to include (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
            />
            {/* v10.19.58: opt-in to attach the file(s) directly. Size
                gate is computed on the SOURCE-FILE total (not a
                post-zip number) so the operator gets the answer
                upfront — never waits for the server to build a zip
                only to be told it was too big. */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={attach && !attachOverLimit}
                    disabled={attachOverLimit}
                    onChange={(_e, v) => setAttach(v)}
                  />
                }
                label={
                  selectedIds.length === 1
                    ? `Attach file to the email (${fmtBytes(totalSourceBytes)})`
                    : `Attach as a zip to the email (${fmtBytes(totalSourceBytes)} uncompressed)`
                }
              />
              {attachOverLimit && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.25 }}>
                  Over the {fmtBytes(ATTACH_MAX_BYTES)} attachment limit — send the link instead.
                </Typography>
              )}
              {attach && !attachOverLimit && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                  The download link goes along as a fallback in case a mail filter strips the attachment.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Emails ship from dcp@hollywoodhills.media. Links expire in 14 days.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {result && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="success" icon={false}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Share created</Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                {result.filename} · {(result.size_bytes / (1024 * 1024)).toFixed(1)} MB
              </Typography>
              {result.emailed_to && (
                <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                  ✓ Emailed to {result.emailed_to}
                </Typography>
              )}
              {result.email_error && (
                <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                  Email failed: {result.email_error}
                </Typography>
              )}
            </Alert>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Share link
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  value={visibleUrl}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={copyLink}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  href={`mailto:?subject=${encodeURIComponent('Key art for this campaign')}&body=${encodeURIComponent(visibleUrl)}`}
                >
                  Email myself
                </Button>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Expires ${new Date(result.expires_at).toLocaleDateString()}`}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!result && (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              variant="outlined"
              onClick={() => handleSubmit(false)}
              disabled={busy || selectedIds.length === 0}
              startIcon={busy ? <CircularProgress size={14} /> : null}
            >
              {busy ? 'Building…' : 'Get share link'}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSubmit(true)}
              disabled={busy || selectedIds.length === 0 || !recipientEmail.trim()}
              startIcon={busy ? <CircularProgress size={14} /> : null}
            >
              {busy ? 'Sending…' : 'Send email'}
            </Button>
          </>
        )}
        {result && (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
